import React from 'react';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from '@mui/material';
import District from '../../model/District';

type Stage3CodePrintPreset = {
	slug: string;
	label: string;
	config: Record<string, any>;
};

type Props = {
	open: boolean;
	onClose: () => void;
	onConfirmDownload: () => void;
	selectedCodesDistrict: string;
	onCodesDistrictChange: (event: SelectChangeEvent<string>) => void;
	eligibleCodeDistricts: District[];
	selectedCodesPrintProfile: string;
	onCodesPrintProfileChange: (event: SelectChangeEvent<string>) => void;
	codesPresets: Stage3CodePrintPreset[];
	codesDefaultPresetSlug: string;
	isCodesPresetsLoading: boolean;
	codesPresetSlugInput: string;
	onCodesPresetSlugChange: (value: string) => void;
	codesPresetLabelInput: string;
	onCodesPresetLabelChange: (value: string) => void;
	codesPresetDraftConfig: Record<string, any>;
	setPresetDraftProperty: (path: string, value: any) => void;
	codesSetAsDefaultOnSave: boolean;
	onCodesSetAsDefaultOnSaveChange: (value: boolean) => void;
	isCodesGridTestEnabled: boolean;
	onCodesGridTestEnabledChange: (value: boolean) => void;
	isCodesPresetSaving: boolean;
	onSavePreset: () => void;
	onSetSelectedPresetAsDefault: () => void;
	isCodesDownloading: boolean;
	isAttendanceDownloading: boolean;
	isCodesGenerating: boolean;
	hasCodesPresetUnsavedChanges: boolean;
	codesUnsavedHighlightSx: Record<string, any>;
};

const Stage3CodesDialog = ({
	open,
	onClose,
	onConfirmDownload,
	selectedCodesDistrict,
	onCodesDistrictChange,
	eligibleCodeDistricts,
	selectedCodesPrintProfile,
	onCodesPrintProfileChange,
	codesPresets,
	codesDefaultPresetSlug,
	isCodesPresetsLoading,
	codesPresetSlugInput,
	onCodesPresetSlugChange,
	codesPresetLabelInput,
	onCodesPresetLabelChange,
	codesPresetDraftConfig,
	setPresetDraftProperty,
	codesSetAsDefaultOnSave,
	onCodesSetAsDefaultOnSaveChange,
	isCodesGridTestEnabled,
	onCodesGridTestEnabledChange,
	isCodesPresetSaving,
	onSavePreset,
	onSetSelectedPresetAsDefault,
	isCodesDownloading,
	isAttendanceDownloading,
	isCodesGenerating,
	hasCodesPresetUnsavedChanges,
	codesUnsavedHighlightSx,
}: Props) => {
	const fitMode = String(codesPresetDraftConfig?.fit?.mode ?? 'columns');
	const isPercentMode = fitMode === 'percent';
	const usesFitWidth = fitMode === 'columns' || fitMode === 'sheet';
	const usesFitHeight = fitMode === 'rows' || fitMode === 'sheet';

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth='sm'
		>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
				<span>Generowanie etykiet kodów</span>
				{hasCodesPresetUnsavedChanges && (
					<Chip
						size='small'
						color='primary'
						label='Niezapisane zmiany'
					/>
				)}
			</DialogTitle>
			<DialogContent>
				<FormControl
					fullWidth
					sx={{ mt: 1 }}
					disabled={isCodesGridTestEnabled}
				>
					<InputLabel id='codes-district-label'>Okręg</InputLabel>
					<Select
						labelId='codes-district-label'
						value={selectedCodesDistrict}
						label='Okręg'
						onChange={onCodesDistrictChange}
					>
						<MenuItem value='all'>Wszystkie okręgi (jedno archiwum ZIP)</MenuItem>
						{eligibleCodeDistricts.map((district: District) => (
							<MenuItem
								key={district.id}
								value={String(district.id)}
							>
								{district.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<FormControl
					fullWidth
					sx={{ mt: 2 }}
				>
					<InputLabel id='codes-print-profile-label'>Profil wydruku</InputLabel>
					<Select
						labelId='codes-print-profile-label'
						value={selectedCodesPrintProfile}
						label='Profil wydruku'
						onChange={onCodesPrintProfileChange}
					>
						{codesPresets.length === 0 && <MenuItem value='default'>Brak presetów (fallback: default)</MenuItem>}
						{codesPresets.map((preset) => (
							<MenuItem
								key={preset.slug}
								value={preset.slug}
							>
								{preset.label} ({preset.slug}){preset.slug === codesDefaultPresetSlug ? ' - domyślny' : ''}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{isCodesPresetsLoading && (
					<Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
						<CircularProgress size={18} />
						<Typography variant='body2'>Wczytywanie presetów...</Typography>
					</Box>
				)}

				<TextField
					fullWidth
					label='Slug presetu'
					value={codesPresetSlugInput}
					onChange={(e) => onCodesPresetSlugChange(e.target.value)}
					sx={{ mt: 2, ...codesUnsavedHighlightSx }}
				/>
				<TextField
					fullWidth
					label='Nazwa presetu'
					value={codesPresetLabelInput}
					onChange={(e) => onCodesPresetLabelChange(e.target.value)}
					sx={{ mt: 2, ...codesUnsavedHighlightSx }}
				/>
				<Typography
					variant='caption'
					sx={{ mt: 1, display: 'block' }}
				>
					Slug to techniczna nazwa pliku (bez spacji, np. biuro-a), a nazwa presetu to etykieta widoczna na liście.
				</Typography>

				<Grid
					container
					spacing={1}
					sx={{ mt: 1, ...codesUnsavedHighlightSx }}
				>
					<Grid
						item
						xs={6}
					>
						<TextField
							fullWidth
							type='number'
							label='Etykiet w wierszu'
							value={codesPresetDraftConfig?.labels_per_row ?? 4}
							onChange={(e) => setPresetDraftProperty('labels_per_row', Number(e.target.value || 4))}
						/>
					</Grid>
					<Grid
						item
						xs={6}
					>
						<TextField
							fullWidth
							type='number'
							label='Wierszy na stronie'
							value={codesPresetDraftConfig?.rows_per_page ?? 10}
							onChange={(e) => setPresetDraftProperty('rows_per_page', Number(e.target.value || 10))}
						/>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<Typography variant='subtitle2'>Szerokości kolumn</Typography>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							type='number'
							label='Pierwsza kolumna'
							value={codesPresetDraftConfig?.col_widths?.first ?? 24.44}
							onChange={(e) => setPresetDraftProperty('col_widths.first', Number(e.target.value || 0))}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							type='number'
							label='Kolumny środkowe'
							value={codesPresetDraftConfig?.col_widths?.middle ?? 24.44}
							onChange={(e) => setPresetDraftProperty('col_widths.middle', Number(e.target.value || 0))}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							type='number'
							label='Ostatnia kolumna'
							value={codesPresetDraftConfig?.col_widths?.last ?? 24.44}
							onChange={(e) => setPresetDraftProperty('col_widths.last', Number(e.target.value || 0))}
						/>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<Typography variant='subtitle2'>Wysokości wierszy</Typography>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							type='number'
							label='Wys. 1. wiersza'
							value={codesPresetDraftConfig?.row_heights?.first ?? 81}
							onChange={(e) => setPresetDraftProperty('row_heights.first', Number(e.target.value || 0))}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							type='number'
							label='Wys. środkowych'
							value={codesPresetDraftConfig?.row_heights?.middle ?? 81}
							onChange={(e) => setPresetDraftProperty('row_heights.middle', Number(e.target.value || 0))}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							type='number'
							label='Wys. ostatniego'
							value={codesPresetDraftConfig?.row_heights?.last ?? 81}
							onChange={(e) => setPresetDraftProperty('row_heights.last', Number(e.target.value || 0))}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							label='Margines lewy'
							value={codesPresetDraftConfig?.margins?.left ?? '0mm'}
							onChange={(e) => setPresetDraftProperty('margins.left', e.target.value)}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							label='Margines prawy'
							value={codesPresetDraftConfig?.margins?.right ?? '0mm'}
							onChange={(e) => setPresetDraftProperty('margins.right', e.target.value)}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							label='Margines górny'
							value={codesPresetDraftConfig?.margins?.top ?? '0mm'}
							onChange={(e) => setPresetDraftProperty('margins.top', e.target.value)}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							label='Margines dolny'
							value={codesPresetDraftConfig?.margins?.bottom ?? '0mm'}
							onChange={(e) => setPresetDraftProperty('margins.bottom', e.target.value)}
						/>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<Typography variant='subtitle2'>Dopasowanie wydruku</Typography>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<FormControl fullWidth>
							<InputLabel id='codes-fit-mode-label'>Tryb dopasowania</InputLabel>
							<Select
								labelId='codes-fit-mode-label'
								value={codesPresetDraftConfig?.fit?.mode ?? 'columns'}
								label='Tryb dopasowania'
								onChange={(e) => setPresetDraftProperty('fit.mode', e.target.value)}
							>
								<MenuItem value='columns'>Do szerokości kolumn</MenuItem>
								<MenuItem value='rows'>Do wysokości wierszy</MenuItem>
								<MenuItem value='sheet'>Do jednej strony</MenuItem>
								<MenuItem value='percent'>Skalowanie procentowe</MenuItem>
								<MenuItem value='none'>Brak dopasowania</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							type='number'
							label='Dopasuj szerokość (strony)'
							value={codesPresetDraftConfig?.fit?.width ?? 1}
							onChange={(e) => setPresetDraftProperty('fit.width', Number(e.target.value || 0))}
							disabled={!usesFitWidth}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							type='number'
							label='Dopasuj wysokość (strony)'
							value={codesPresetDraftConfig?.fit?.height ?? 0}
							onChange={(e) => setPresetDraftProperty('fit.height', Number(e.target.value || 0))}
							disabled={!usesFitHeight}
						/>
					</Grid>
					<Grid
						item
						xs={4}
					>
						<TextField
							fullWidth
							type='number'
							label='Skalowanie %'
							value={codesPresetDraftConfig?.fit?.zoom ?? 100}
							onChange={(e) => setPresetDraftProperty('fit.zoom', Number(e.target.value || 100))}
							disabled={!isPercentMode}
						/>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<Typography variant='caption'>
							`Skalowanie %` działa tylko dla trybu `Skalowanie procentowe`. `Dopasuj szerokość/wysokość` działa dla
							trybów dopasowania stron (`columns`, `rows`, `sheet`).
						</Typography>
					</Grid>
				</Grid>

				<FormControlLabel
					control={
						<Checkbox
							checked={codesSetAsDefaultOnSave}
							onChange={(e) => onCodesSetAsDefaultOnSaveChange(e.target.checked)}
						/>
					}
					label='Ustaw jako domyślny po zapisie'
					sx={{ mt: 1 }}
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={isCodesGridTestEnabled}
							onChange={(e) => onCodesGridTestEnabledChange(e.target.checked)}
						/>
					}
					label='Linie siatki = TEST (ustawienie tylko na tę sesję)'
				/>
				{isCodesGridTestEnabled && (
					<Typography variant='body2'>Wygeneruje pojedynczy plik XLSX z testowymi danymi na jedną stronę.</Typography>
				)}

				<Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
					{hasCodesPresetUnsavedChanges && (
						<Button
							variant='outlined'
							onClick={onSavePreset}
							disabled={isCodesPresetSaving || isCodesPresetsLoading}
						>
							{isCodesPresetSaving ? 'Zapisywanie...' : 'Zapisz preset'}
						</Button>
					)}
					<Button
						variant='outlined'
						onClick={onSetSelectedPresetAsDefault}
						disabled={isCodesPresetSaving || isCodesPresetsLoading || !selectedCodesPrintProfile}
					>
						Ustaw wybrany jako domyślny
					</Button>
				</Box>

				<Typography
					variant='body2'
					sx={{ mt: 2 }}
				>
					{isCodesGridTestEnabled
						? 'Wygeneruje pojedynczy plik XLSX z testowymi danymi na jedną stronę.'
						: selectedCodesDistrict === 'all'
							? `Wygeneruje ZIP z osobnym plikiem dla każdego okręgu z uczniami zakwalifikowanymi do etapu III (${eligibleCodeDistricts.length} plików).`
							: 'Wygeneruje pojedynczy plik XLSX dla wybranego okręgu.'}
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Anuluj</Button>
				<Button
					variant='contained'
					onClick={onConfirmDownload}
					disabled={
						isCodesDownloading ||
						isAttendanceDownloading ||
						isCodesGenerating ||
						isCodesPresetsLoading ||
						isCodesPresetSaving
					}
				>
					Generuj i pobierz
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default Stage3CodesDialog;
